import { useState } from "react";
import dayjs from "dayjs";
import { PrimaryButton } from "@normasteaching/norma-ui";
import {
  BonificoOrderDTO,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { awaitingInvoiceOrdersQuery } from "../../ProductService/BonificoOrder/BonificoOrdersListQuery.ts";
import { useBonificoOrderInvoicing } from "../../ProductService/BonificoOrder/useBonificoOrderInvoicing.ts";
import { useBonificoOrdersPage } from "../../ProductService/BonificoOrder/useBonificoOrdersPage.ts";
import { useLastInvoicedValueDate } from "../../ProductService/BonificoOrder/useLastInvoicedValueDate.ts";
import {
  isArticle7Ter,
  vatExemptionFor,
} from "../../ProductService/BonificoOrder/article7Ter.ts";
import { BonificoBillingSummary } from "./BonificoBillingSummary.tsx";
import { Article7TerSwitch } from "./Article7TerSwitch.tsx";
import { Article7TerBadge } from "./Article7TerBadge.tsx";
import { InvoiceItemDescriptionInput } from "./InvoiceItemDescriptionInput.tsx";
import {
  chosenInvoiceItemDescription,
  invoiceItemDescriptionPlaceholderOf,
} from "../../ProductService/BonificoOrder/invoiceItemDescription.ts";
import { EditBonificoBillingModal } from "./EditBonificoBillingModal.tsx";
import { describeInvoiceIssue } from "./describeInvoiceIssue.ts";
import { OverlayDialog } from "../../UI/Modal/OverlayDialog.tsx";

function formatDate(dateUtc: string): string {
  return dayjs(dateUtc).format("DD/MM/YYYY");
}

export function IssueInvoiceModal({
  order: initialOrder,
  onSuccess,
  onClose,
  onOrderUpdated,
}: {
  order: BonificoOrderDTO;
  onSuccess: () => void;
  onClose: () => void;
  onOrderUpdated?: (order: BonificoOrderDTO) => void;
}) {
  const [order, setOrder] = useState(initialOrder);
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isArticle7TerApplied, setIsArticle7TerApplied] = useState(
    isArticle7Ter(initialOrder.vatExemption),
  );
  const [invoiceItemDescription, setInvoiceItemDescription] = useState(
    initialOrder.invoiceItemDescription ?? "",
  );
  const { issueInvoice, loading } = useBonificoOrderInvoicing();
  const { lastValueDateUtc } = useLastInvoicedValueDate();
  const valueDateUtc = order.valueDateUtc ?? "";
  const { orders: earlierAwaitingOrders } = useBonificoOrdersPage(
    awaitingInvoiceOrdersQuery(
      dayjs(valueDateUtc).subtract(1, "day").format("YYYY-MM-DD"),
    ),
  );
  const isBeforeLastInvoice =
    !!lastValueDateUtc &&
    dayjs(valueDateUtc).format("YYYY-MM-DD") <
      dayjs(lastValueDateUtc).format("YYYY-MM-DD");

  const handleConfirm = async () => {
    setError(null);
    try {
      await issueInvoice(order.orderId, {
        vatExemption: vatExemptionFor(isArticle7TerApplied),
        invoiceItemDescription: chosenInvoiceItemDescription(
          invoiceItemDescription,
        ),
      });
      onSuccess();
    } catch (e) {
      setError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante l'emissione della fattura. Riprova.",
        ),
      );
    }
  };

  return (
    <OverlayDialog
      onClose={onClose}
      isClosable={!loading}
      panelClassName="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4"
    >
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-bold">Emetti fattura</h2>
        {isArticle7TerApplied && <Article7TerBadge />}
      </div>
      <p className="text-sm text-gray-600">
        Ordine: <span className="font-mono text-xs">{order.orderId}</span>
        <br />
        Cliente: <strong>{order.customer.email}</strong>
        <br />
        Data valuta: <strong>{formatDate(valueDateUtc)}</strong>
      </p>
      <BonificoBillingSummary
        order={order}
        onEdit={() => setIsEditingBilling(true)}
      />
      <Article7TerSwitch
        checked={isArticle7TerApplied}
        onChange={setIsArticle7TerApplied}
      />
      <InvoiceItemDescriptionInput
        value={invoiceItemDescription}
        placeholder={invoiceItemDescriptionPlaceholderOf(order)}
        onChange={setInvoiceItemDescription}
      />
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
        {describeInvoiceIssue(order)} È l'ultimo passaggio: dopo la data valuta
        e i dati di fatturazione non si possono più modificare da qui.
      </div>
      {isBeforeLastInvoice && (
        <p className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800">
          La data valuta è precedente all'ultima fattura emessa (
          {formatDate(lastValueDateUtc!)}): modificala prima di emettere la
          fattura.
        </p>
      )}
      {earlierAwaitingOrders.length > 0 && (
        <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
          {earlierAwaitingOrders.length === 1
            ? "C'è 1 ordine"
            : `Ci sono ${earlierAwaitingOrders.length} ordini`}{" "}
          con fattura da emettere e data valuta precedente (dal{" "}
          {formatDate(earlierAwaitingOrders[0].valueDateUtc!)}). Emettili prima,
          anche in blocco con «Emetti fatture», altrimenti non potranno più
          essere fatturati con la loro data valuta.
        </p>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-3 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
        >
          Annulla
        </button>
        <PrimaryButton
          label={loading ? "Emissione..." : "Emetti fattura"}
          onClick={handleConfirm}
          disabled={loading}
        />
      </div>
      {isEditingBilling && (
        <EditBonificoBillingModal
          order={order}
          onSaved={(updated) => {
            setOrder(updated);
            setIsArticle7TerApplied(isArticle7Ter(updated.vatExemption));
            setIsEditingBilling(false);
            onOrderUpdated?.(updated);
          }}
          onClose={() => setIsEditingBilling(false)}
        />
      )}
    </OverlayDialog>
  );
}
